import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  isWebNotificationSupported,
  getWebNotificationPermission,
  requestWebNotificationPermission,
  hasSoftPromptedForWebNotifications,
  markSoftPromptedForWebNotifications,
} from "@/lib/webNotifications";

/**
 * One-time soft in-app ask to enable browser notifications. We show a small
 * dismissible card BEFORE calling Notification.requestPermission(), so the
 * user isn't hit by a cold browser prompt they reflexively deny. After the
 * user picks either button (or dismisses), we mark it so it never auto-opens
 * again. Users can still enable later from Settings → Sound & Notifications.
 */
export function WebNotificationSoftPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isWebNotificationSupported()) return;
    if (getWebNotificationPermission() !== "default") return;
    if (hasSoftPromptedForWebNotifications()) return;

    // Give the app a beat to render so the card isn't the first thing
    // that appears on screen post-login.
    const t = window.setTimeout(() => setVisible(true), 2500);
    return () => window.clearTimeout(t);
  }, [user]);

  if (!visible) return null;

  const close = () => {
    markSoftPromptedForWebNotifications();
    setVisible(false);
  };

  const enable = async () => {
    await requestWebNotificationPermission();
    close();
  };

  return (
    <div
      role="dialog"
      aria-label="Enable alerts"
      className="fixed bottom-4 right-4 z-[60] w-[calc(100vw-2rem)] max-w-sm rounded-lg border border-border bg-card shadow-lg animate-in fade-in slide-in-from-bottom-4"
    >
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="p-4 pr-8">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
            <Bell className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">Turn on alerts?</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Hear a chime and see a pop-up when new bids, quotes, and messages come in — even while you're on another tab.
            </p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={enable}>Enable alerts</Button>
              <Button size="sm" variant="ghost" onClick={close}>Not now</Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              You can change this any time in Settings → Sound &amp; Notifications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
